import React, { useContext, useEffect } from 'react';
import { toast } from 'react-toastify';
import { downloadFolder } from '../../../../services/api';
import ThemeContext from '../../../../ThemeContext';

const FoldersPanel = ({
  folders,
  selectedFolder,
  selectedFolders,
  onFolderSelect,
  onFolderCheckbox,
  onAddFolder,
  onRenameFolder,
  onMoveFolder,
  onCopyFolder,
  onDeleteFolder,
  isLoading,
  caseId,
}) => {
  const { theme } = useContext(ThemeContext);

const user = JSON.parse(localStorage.getItem("user"));
const userRole = user?.role;
const isClientUser = userRole === "client_user";


  const handleDownloadFolders = async () => {
    if (selectedFolders.length === 0) {
      toast.warning('Please select folders to download', {
        style: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.warning,
        },
      });
      return;
    }

    for (const folderId of selectedFolders) {
      try {
        const folder = folders.find(f => f.id === folderId);
        console.log('📥 Starting download for folder:', folderId);
        const response = await downloadFolder(folderId);

        if (response.data instanceof Blob) {
            if (response.data.type === 'application/json') {
              const text = await response.data.text();
              const errorData = JSON.parse(text);
              toast.error(errorData.message || 'Failed to download folder');
              continue;
            }

            const blob = new Blob([response.data], { type: 'application/zip' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${folder.name.replace(/[^a-z0-9\s]/gi, '_')}_${new Date().toISOString().split('T')[0]}.zip`;

            document.body.appendChild(link);
            setTimeout(() => {
              link.click();
              console.log(' Download clicked for:', link.download);
              setTimeout(() => {
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                console.log('🧹 Cleaned up download link');
              }, 500);
            }, 100);

            toast.success(`${folder.name} download started`, {
              style: {
                backgroundColor: theme.colors.bgPrimary,
                border: `1px solid ${theme.colors.borderColor}`,
                color: theme.colors.success,
              },
            });
          }
        } catch (error) {
          console.error('❌ Download error:', error);
          let errorMessage = 'Failed to download folder';
          if (error.response?.data instanceof Blob) {
            try {
              const text = await error.response.data.text();
              const errorData = JSON.parse(text);
              errorMessage = errorData.message || errorMessage;
            } catch (e) {
              console.error('Could not parse error:', e);
            }
          } else if (error.response?.data?.message) {
            errorMessage = error.response.data.message;
          } else if (error.message) {
            errorMessage = error.message;
          }

          toast.error(errorMessage, {
            style: {
              backgroundColor: theme.colors.bgPrimary,
              border: `1px solid ${theme.colors.borderColor}`,
              color: theme.colors.danger,
            },
          });
        }
    }
  };

  const handleSelectAll = () => {
    const selectableFolders = folders.filter(f => !f.is_default).map(f => f.id);
    if (selectedFolders.length === selectableFolders.length) {
      console.log('Clearing all selected folders');
      onFolderCheckbox('clear');
    } else {
      console.log('Selecting all available folders:', selectableFolders);
      selectableFolders.forEach(id => {
        if (!selectedFolders.includes(id)) {
          onFolderCheckbox(id);
        }
      });
    }
  };

  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        border: `1px solid ${theme.colors.borderColor}`,
        borderRadius: '8px',
        backgroundColor: theme.colors.bgSecondary,
        minHeight: '400px',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 'clamp(0.5rem, 2vw, 0.75rem)',
          borderBottom: `1px solid ${theme.colors.borderColor}`,
          backgroundColor: theme.colors.bgPrimary,
          borderTopLeftRadius: '8px',
          borderTopRightRadius: '8px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <i className="bi bi-folder-fill" style={{ color: theme.colors.primary, fontSize: 'clamp(12px, 2.5vw, 14px)' }}></i>
          <span style={{ fontWeight: 'bold', fontSize: 'clamp(10px, 2.5vw, 11px)', color: theme.colors.textPrimary }}>
            Folders ({folders.length})
          </span>
        </div>
        {!isClientUser && (
        <button
          onClick={onAddFolder}
          style={{
            background: theme.gradients.primary,
            color: theme.colors.textWhite,
            border: 'none',
            padding: 'clamp(4px, 1.5vw, 6px) clamp(8px, 2vw, 10px)',
            borderRadius: '5px',
            fontSize: 'clamp(9px, 2vw, 10px)',
            cursor: 'pointer',
            transition: 'opacity 0.3s ease',
          }}
          onMouseOver={(e) => (e.target.style.opacity = '0.9')}
          onMouseOut={(e) => (e.target.style.opacity = '1')}
        >
          Add Folder
        </button>
        )}
      </div>

      {/* Folders List */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: 'clamp(0.5rem, 2vw, 0.75rem)',
          maxHeight: 'calc(8 * 45px)',
        }}
      >
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: theme.colors.textSecondary, fontSize: 'clamp(9px, 2vw, 10px)' }}>
            Loading folders...
          </div>
        ) : folders.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: theme.colors.textSecondary, fontSize: 'clamp(9px, 2vw, 10px)' }}>
            No folders available
          </div>
        ) : (
          <>
          {!isClientUser && (

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: 'clamp(0.4rem, 1.5vw, 0.6rem)',
                marginBottom: '2px',
                borderRadius: '5px',
                backgroundColor: theme.colors.bgSecondary,
                border: `1px solid ${theme.colors.borderColor}`,
                fontSize: 'clamp(9px, 2vw, 10px)',
              }}
            >
              <input
  type="checkbox"
  checked={
    selectedFolders.length > 0 &&
    selectedFolders.length === folders.filter(f => !f.is_default).length
  }
  onChange={handleSelectAll}
  disabled={isClientUser}
/>
              <span style={{ color: theme.colors.textPrimary, fontWeight: 'bold' }}>
                Select All
              </span>
            </div>
            )}

            {folders.map((folder) => (
              <div
                key={folder.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: 'clamp(0.4rem, 1.5vw, 0.6rem)',
                  marginBottom: '2px',
                  borderRadius: '5px',
                  backgroundColor: selectedFolder?.id === folder.id ? '#ff9933' : theme.colors.bgPrimary,
                  border: `1px solid ${theme.colors.borderColor}`,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  fontSize: 'clamp(9px, 2vw, 10px)',
                }}
                onClick={() => onFolderSelect(folder)}
                onMouseOver={(e) => {
                  if (selectedFolder?.id !== folder.id) {
                    e.currentTarget.style.backgroundColor = theme.colors.bgSecondary;
                  }
                }}
                onMouseOut={(e) => {
                  if (selectedFolder?.id !== folder.id) {
                    e.currentTarget.style.backgroundColor = theme.colors.bgPrimary;
                  }
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1 }}>
                  <input
                    type="checkbox"
                    checked={selectedFolders.includes(folder.id)}
                    onChange={(e) => {
                      e.stopPropagation();
                      console.log(`Checkbox toggled for folder ID ${folder.id}`);
                      onFolderCheckbox(folder.id);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    disabled={folder.is_default}
                    style={{ cursor: folder.is_default ? 'not-allowed' : 'pointer' }}
                  />
                  <i className="bi bi-folder" style={{ color: theme.colors.primary }}></i>
                  <span style={{ color: selectedFolder?.id === folder.id ? '#fff' : theme.colors.textPrimary }}>
                    {folder.name}
                  </span>
                  {folder.is_default && (
                    <span style={{ 
                      fontSize: 'clamp(7px, 1.5vw, 8px)', 
                      color: selectedFolder?.id === folder.id ? '#fff' : theme.colors.textSecondary,
                      marginLeft: '0.25rem',
                      fontStyle: 'italic'
                    }}>
                      (Default)
                    </span>
                  )}
                </div>
                {!folder.is_default && !isClientUser && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onRenameFolder(folder);
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: selectedFolder?.id === folder.id ? '#fff' : theme.colors.primary,
                      fontSize: 'clamp(8px, 2vw, 9px)',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      padding: '0',
                    }}
                  >
                    Rename
                  </button>
                )}
              </div>
            ))}
          </>
        )}
      </div>

      {/* Action Buttons */}
      <div
        style={{
          display: 'flex',
          gap: '0.5rem',
          padding: 'clamp(0.5rem, 2vw, 0.75rem)',
          borderTop: `1px solid ${theme.colors.borderColor}`,
          backgroundColor: theme.colors.bgSecondary,
          borderBottomLeftRadius: '8px',
          borderBottomRightRadius: '8px',
        }}
      >
        <span style={{ fontSize: 'clamp(8px, 2vw, 9px)', color: theme.colors.textSecondary, alignSelf: 'center' }}>
          Selected ({selectedFolders.length})
        </span>
        <button
          onClick={onCopyFolder}
          disabled={selectedFolders.length === 0}
          style={{
            flex: 1,
            padding: 'clamp(4px, 1.5vw, 6px)',
            fontSize: 'clamp(8px, 2vw, 9px)',
            border: 'none',
            borderRadius: '4px',
            cursor: selectedFolders.length === 0 ? 'not-allowed' : 'pointer',
            backgroundColor: selectedFolders.length === 0 ? theme.colors.bgSecondary : theme.colors.info,
            color: selectedFolders.length === 0 ? theme.colors.textSecondary : theme.colors.textWhite,
            opacity: selectedFolders.length === 0 ? 0.5 : 1,
          }}
        >
          Copy
        </button>
        <button
          onClick={handleDownloadFolders}
          disabled={selectedFolders.length === 0}
          style={{
            flex: 1,
            padding: 'clamp(4px, 1.5vw, 6px)',
            fontSize: 'clamp(8px, 2vw, 9px)',
            border: 'none',
            borderRadius: '4px',
            cursor: selectedFolders.length === 0 ? 'not-allowed' : 'pointer',
            backgroundColor: selectedFolders.length === 0 ? theme.colors.bgSecondary : theme.colors.success,
            color: selectedFolders.length === 0 ? theme.colors.textSecondary : theme.colors.textWhite,
            opacity: selectedFolders.length === 0 ? 0.5 : 1,
          }}
        >
          Download
        </button>
        <button
          onClick={onMoveFolder}
          disabled={selectedFolders.length === 0}
          style={{
            flex: 1,
            padding: 'clamp(4px, 1.5vw, 6px)',
            fontSize: 'clamp(8px, 2vw, 9px)',
            border: 'none',
            borderRadius: '4px',
            cursor: selectedFolders.length === 0 ? 'not-allowed' : 'pointer',
            backgroundColor: selectedFolders.length === 0 ? theme.colors.bgSecondary : theme.colors.primary,
            color: selectedFolders.length === 0 ? theme.colors.textSecondary : theme.colors.textWhite,
            opacity: selectedFolders.length === 0 ? 0.5 : 1,
          }}
        >
          Move
        </button>
        <button
          onClick={onDeleteFolder}
          disabled={selectedFolders.length === 0}
          style={{
            flex: 1,
            padding: 'clamp(4px, 1.5vw, 6px)',
            fontSize: 'clamp(8px, 2vw, 9px)',
            border: 'none',
            borderRadius: '4px',
            cursor: selectedFolders.length === 0 ? 'not-allowed' : 'pointer',
            backgroundColor: selectedFolders.length === 0 ? theme.colors.bgSecondary : theme.colors.danger,
            color: selectedFolders.length === 0 ? theme.colors.textSecondary : theme.colors.textWhite,
            opacity: selectedFolders.length === 0 ? 0.5 : 1,
          }}
        >
          Delete
        </button>
      </div>
    </div>
  );
};

export default FoldersPanel;